/**
 * Table half-court split.
 * Spectator: top-left → bottom-right diagonal.
 * Referee: net line through table center (mid far edge → mid near edge).
 * Mirrors Backend/pose_worker/pose_core/table_zones.py.
 */

import {
  cameraSetupFromViewSetup,
  geometryModeFromSetup,
} from "./evaluationCameraSetup";
import { mapStorageToDisplayNorm } from "./videoOrientCoords";

function cornersForAnalysis(corners, viewSetup) {
  if (!Array.isArray(corners) || corners.length < 4) return corners;
  if (!viewSetup) return corners;
  return corners.map((c) => {
    const m = mapStorageToDisplayNorm(c.x, c.y, viewSetup);
    return { x: m.x, y: m.y };
  });
}

function pointForAnalysis(xNorm, yNorm, viewSetup) {
  if (!viewSetup) return { x: Number(xNorm), y: Number(yNorm) };
  return mapStorageToDisplayNorm(Number(xNorm), Number(yNorm), viewSetup);
}

export function geometryModeFromViewSetup(viewSetup) {
  const vs =
    viewSetup && typeof viewSetup === "object" && !Array.isArray(viewSetup)
      ? viewSetup
      : {};
  const nested =
    vs.user && typeof vs.user === "object" && !Array.isArray(vs.user)
      ? vs.user
      : null;
  return geometryModeFromSetup(
    cameraSetupFromViewSetup(vs) || cameraSetupFromViewSetup(nested)
  );
}

function orientLeftPositive(sx, sy, ex, ey, corners) {
  const tl = corners[0];
  const bl = corners[3];
  const gAx = (tl.x + bl.x) / 2;
  const gAy = (tl.y + bl.y) / 2;
  const crossG = (ex - sx) * (gAy - sy) - (ey - sy) * (gAx - sx);
  if (crossG < 0) {
    return { sx: ex, sy: ey, ex: sx, ey: sy };
  }
  return { sx, sy, ex, ey };
}

function playerSplitDiagonal(corners) {
  if (!Array.isArray(corners) || corners.length < 4) {
    return { sx: 0, sy: 0, ex: 1, ey: 1 };
  }
  const [tl, , br] = corners;
  return orientLeftPositive(tl.x, tl.y, br.x, br.y, corners);
}

function netSplitLine(corners) {
  if (!Array.isArray(corners) || corners.length < 4) {
    return { sx: 0.5, sy: 0, ex: 0.5, ey: 1 };
  }
  const [tl, tr, br, bl] = corners;
  const farX = (tl.x + tr.x) / 2;
  const farY = (tl.y + tr.y) / 2;
  const nearX = (bl.x + br.x) / 2;
  const nearY = (bl.y + br.y) / 2;
  return orientLeftPositive(farX, farY, nearX, nearY, corners);
}

function playerSplitLine(corners, geometryMode = "spectator") {
  if (String(geometryMode || "").toLowerCase().startsWith("ref")) {
    return netSplitLine(corners);
  }
  return playerSplitDiagonal(corners);
}

/** True if (x,y) is on Player A / G side of the camera-setup split line. */
export function isOnTableLeftHalf(xNorm, yNorm, corners, viewSetup = null) {
  if (!Array.isArray(corners) || corners.length < 4) {
    return Number(xNorm) <= 0.5;
  }
  const c = cornersForAnalysis(corners, viewSetup);
  const p = pointForAnalysis(xNorm, yNorm, viewSetup);
  const { sx, sy, ex, ey } = playerSplitLine(
    c,
    geometryModeFromViewSetup(viewSetup)
  );
  const cross = (ex - sx) * (p.y - sy) - (ey - sy) * (p.x - sx);
  return cross >= 0;
}

export function roiCenterNorm(roi) {
  return {
    x: (roi?.x || 0) + (roi?.w || 0) / 2,
    y: (roi?.y || 0) + (roi?.h || 0) / 2,
  };
}

/** SVG line endpoints in contain-rect pixel space for the table split. */
export function splitDiagonalLinePx(corners, contain, viewSetup = null) {
  if (!Array.isArray(corners) || corners.length < 4 || !contain) return null;
  const { sx, sy, ex, ey } = playerSplitLine(
    corners,
    geometryModeFromViewSetup(viewSetup)
  );
  return {
    x1: contain.x + sx * contain.w,
    y1: contain.y + sy * contain.h,
    x2: contain.x + ex * contain.w,
    y2: contain.y + ey * contain.h,
  };
}

/**
 * Order ROIs by table half (G / D) when corners exist;
 * otherwise fall back to screen-left ordering with mirror.
 */
export function orderRoisForTableHalf(
  roiA,
  roiB,
  validA,
  validB,
  corners,
  mirrored,
  viewSetup = null
) {
  if (!roiA || !roiB) {
    return { roiLeft: roiA, roiRight: roiB, validLeft: validA, validRight: validB };
  }
  if (Array.isArray(corners) && corners.length === 4) {
    const ca = roiCenterNorm(roiA);
    const cb = roiCenterNorm(roiB);
    const aLeft = isOnTableLeftHalf(ca.x, ca.y, corners, viewSetup);
    const bLeft = isOnTableLeftHalf(cb.x, cb.y, corners, viewSetup);
    if (aLeft && !bLeft) {
      return { roiLeft: roiA, roiRight: roiB, validLeft: validA, validRight: validB };
    }
    if (bLeft && !aLeft) {
      return { roiLeft: roiB, roiRight: roiA, validLeft: validB, validRight: validA };
    }
  }
  const cxA = mirrored ? 1 - roiCenterNorm(roiA).x : roiCenterNorm(roiA).x;
  const cxB = mirrored ? 1 - roiCenterNorm(roiB).x : roiCenterNorm(roiB).x;
  if (cxA <= cxB) {
    return { roiLeft: roiA, roiRight: roiB, validLeft: validA, validRight: validB };
  }
  return { roiLeft: roiB, roiRight: roiA, validLeft: validB, validRight: validA };
}

/** True when two joint sets likely belong to the same person. */
export function jointsSamePerson(jointsA, jointsB, imageWidth = 1) {
  if (!Array.isArray(jointsA) || !Array.isArray(jointsB)) return false;
  const torsoIdx = [5, 6, 11, 12];
  const xsA = [];
  const xsB = [];
  for (const i of torsoIdx) {
    const ja = jointsA[i];
    const jb = jointsB[i];
    if (ja && (ja.score ?? 1) >= 0.2) xsA.push(ja.x);
    if (jb && (jb.score ?? 1) >= 0.2) xsB.push(jb.x);
  }
  if (!xsA.length || !xsB.length) return false;
  const cxA = xsA.reduce((a, b) => a + b, 0) / xsA.length;
  const cxB = xsB.reduce((a, b) => a + b, 0) / xsB.length;
  const w = Math.max(Number(imageWidth) || 1, 1);
  return Math.abs(cxA - cxB) * w < w * 0.06;
}
